import React, { useEffect, useState, ReactNode, useRef } from "react";
import { Message, Transition } from "semantic-ui-react";
import { SemanticCOLORS } from "semantic-ui-react/dist/commonjs/generic";
import { getErrorColor, getErrorMessage } from "../../../Helpers";
import { IErrorResponse } from "../../interfaces/Global";
import BCNewLine from "../../ui/BCNewLine";
import "../../../styles/ui.scss";
import { GlobalConstants } from "../../constants/GlobalConstants";
import { isInViewport } from "../../../helpers/GlobalHelper";

interface IBPProps {
  animation?: string;
  duration?: number;
  visible?: any;
  transitionOnMount?: boolean;
  messageStyles?: string;
  negative?: boolean;
  color?: SemanticCOLORS;
  onDismiss?: any;
  compact?: boolean;
  header?: string;
  clickable?: any;
  text?: any;
  errorResponse?: IErrorResponse;
  autoCloseIn?: number;
  skipAutoClose?: boolean;
  isInfo?: boolean;
  onlyHeader?: boolean
  funcOnClose?: () => any
  textNode?: ReactNode
  skipDismiss?: boolean
  skipHeader?: boolean
  indentLines?: boolean
  error?: any
}

const LmsMessageText = ({ errorText, clickable, indentLines = false }: any) => {
  if (errorText && typeof errorText === "string" && !errorText.includes("\n")) {
    return <React.Fragment>
      {clickable ? (
        <div className="cursor-pointer" onClick={clickable}>
          {errorText}
        </div>
      ) : (
        <p>{errorText}</p>
      )}
    </React.Fragment>;
  }
  else {
    if (React.isValidElement(errorText)) {
      return errorText;
    }
    else if (Array.isArray(errorText)) {
      return <BCNewLine text={errorText} indentLines={indentLines} />;
    }
    else if (errorText && typeof errorText === "string" && errorText.includes("\n")) {
      return <BCNewLine text={errorText} indentLines={indentLines} />;
    }
  }

  return <React.Fragment />;
};

const LmsMessage = ({ ...props }: IBPProps) => {
  const errorResponse = props.error?.errorResponse || props.errorResponse;
  let message = props.error?.message || props.textNode || props.text as string | any[] | ReactNode;
  const isNegative = props.isInfo ? false : props.negative === undefined || props.negative;
  const ref = useRef(null as any);

  if (errorResponse && errorResponse.errorMessage) {
    const errorText = getErrorMessage(errorResponse?.errorId, errorResponse?.errorMessage);
    if (errorText && errorText.includes("\n")) {
      message = <BCNewLine text={errorText}/>;
    }
  }

  const isMessageVisible = props.visible;
  const [isVisible, setIsVisible] = useState(isMessageVisible === undefined);

  const handleDismiss = () => {
    if (props.onDismiss && typeof props.onDismiss === "function") {
      props.onDismiss();
    }
    else if (props.funcOnClose && typeof props.funcOnClose === "function") {
      props.funcOnClose();
    }
    else {
      if (isVisible) {
        setIsVisible(false);
      }
    }
  };

  useEffect(() => {
    if (isVisible && message && isNegative && !isInViewport(ref?.current) && ref?.current?.scrollIntoView) {
      ref?.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [isVisible, message]);

  useEffect(() => {
    let timoutFunc = null as any;
    let handleDismissFunc = null as any;
    if ((props.autoCloseIn || !props.skipAutoClose) && (isMessageVisible || isVisible)) {
      timoutFunc = setTimeout(() => {
        setIsVisible(false);

        handleDismissFunc = setTimeout(() => {
          handleDismiss();
        }, 300);
      }, props.autoCloseIn || GlobalConstants.DefaultValues.messageAutoCloseIn);
    }

    return () => {
      if (timoutFunc) {
        clearTimeout(timoutFunc);
      }

      if (handleDismissFunc) {
        clearTimeout(timoutFunc);
      }
    };
  }, [isMessageVisible, isVisible]);

  return (
    <div ref={ref}>
      <Transition
        animation={props.animation || GlobalConstants.DefaultValues.messageAnimation}
        duration={props.duration || GlobalConstants.DefaultValues.messageTransitionDuration}
        visible={isMessageVisible || isVisible}
        transitionOnMount={props.transitionOnMount || true}
      >
        {errorResponse ? (
          <Message
            className={props.messageStyles || GlobalConstants.DefaultValues.messageStyles}
            negative={isNegative}
            color={props.isInfo ? GlobalConstants.DefaultValues.messageInfoColor as SemanticCOLORS : getErrorColor(errorResponse && errorResponse.errorLevel ? errorResponse.errorLevel : "red")}
            compact={props.compact}
            onDismiss={!props.skipDismiss ? handleDismiss : undefined}
          >
            <Message.Header>
              {errorResponse.errorTitle ? errorResponse.errorTitle : ""}
            </Message.Header>
            <LmsMessageText errorText={message} clickable={props.clickable} indentLines={!!props.indentLines} />
          </Message>
        ) : (
          <Message
            className={props.messageStyles || GlobalConstants.DefaultValues.messageStyles}
            negative={props.isInfo ? false : props.negative === undefined || props.negative}
            color={props.isInfo ? GlobalConstants.DefaultValues.messageInfoColor as SemanticCOLORS : props.color || GlobalConstants.DefaultValues.messageDefaultColor as SemanticCOLORS}
            compact={props.compact}
            onDismiss={!props.skipDismiss ? handleDismiss : undefined}
          >
            {!props.skipHeader && <Message.Header>{props.header ? props.header : !props.isInfo ? GlobalConstants.DefaultValues.messageHeaderText : ""}</Message.Header>}
            {!props.onlyHeader && <LmsMessageText errorText={message} clickable={props.clickable} indentLines={!!props.indentLines} />}
          </Message>
        )}
      </Transition>
    </div>
  );
};

export default LmsMessage;